<script lang="ts">
  import { onMount } from 'svelte'
  import { navigate } from 'svelte-routing'
  import { submitIntake } from '$lib/public/intake-client'
  import { showApiError } from '$lib/util/errors'
  import {
    emptyDraft,
    canGoNext,
    toIntakeBody,
    seedDraft,
    isStepComplete,
    firstIncompleteStep,
    hasAnyPrefill,
    type Step,
    type Prefill,
  } from '$lib/public/wizard-state'
  import { fetchBranding, type Branding } from '$lib/public/branding-client'
  import { onAccentTextColor } from '$lib/util/accent'
  import { brandStore } from '$lib/public/brand-store.svelte'
  import AnimalStep from './AnimalStep.svelte'
  import PersonStep from './PersonStep.svelte'
  import UnitStep from './UnitStep.svelte'
  import StatusStep from './StatusStep.svelte'
  import ReviewStep from './ReviewStep.svelte'

  interface Props {
    pmSlug?: string | undefined
    propertySlug?: string | undefined
    // When embedded via /embed/:token, threads the session token into the intake
    // submission (linking + completing the session) and fires onComplete on success.
    sessionToken?: string | undefined
    // Partner-supplied prefill (embed only). When non-empty, the wizard seeds the draft,
    // skips fully-prefilled steps, and shows a review screen before submit.
    prefill?: Prefill | undefined
    onComplete?: ((confirmationId: number) => void) | undefined
  }
  let { pmSlug, propertySlug, sessionToken, prefill, onComplete }: Props = $props()

  let branding = $state<Branding | null>(null)
  let brandLoaded = $state(false)

  onMount(async () => {
    if (pmSlug && propertySlug) branding = await fetchBranding(pmSlug, propertySlug)
    brandStore.current = branding
    brandLoaded = true
  })

  const accentStyle = $derived(
    branding?.pm.accentColor
      ? `--brand: ${branding.pm.accentColor}; --brand-hover: ${branding.pm.accentColor}; --on-brand: ${onAccentTextColor(branding.pm.accentColor)};`
      : '',
  )
  const propertyId = $derived(branding?.property.id)

  const STEPS: Step[] = ['animal', 'status', 'person', 'unit']
  const STEP_LABELS: Record<Step, string> = {
    animal: 'Animal',
    status: 'Status',
    person: 'Contact',
    unit: 'Unit',
  }

  // prefill is a stable input (Embed resolves it once and never changes it), so seeding the
  // draft + computing the visible steps from its initial value is intentional, not a missed
  // reactive dependency.
  // svelte-ignore state_referenced_locally
  const prefilled = hasAnyPrefill(prefill)
  // svelte-ignore state_referenced_locally
  let draft = $state(prefilled && prefill ? seedDraft(prefill) : emptyDraft())

  // Which steps the applicant walks. When prefilled, drop the steps already complete at
  // seed time (but always keep the classifier 'status', which is never prefilled). Computed
  // once from the seeded draft so it doesn't churn as the classifier gets filled in; an Edit
  // from the review screen adds a step back via `editedBack`.
  const baseVisible = new Set<Step>(
    prefilled ? STEPS.filter((s) => s === 'status' || !isStepComplete(s, draft)) : STEPS,
  )
  let editedBack = $state<Set<Step>>(new Set())
  const visibleSteps = $derived(STEPS.filter((s) => baseVisible.has(s) || editedBack.has(s)))

  let step = $state<Step>(prefilled ? firstIncompleteStep(draft) : 'animal')
  let reviewing = $state(false)
  let cameFromReview = $state(false)
  let submitting = $state(false)

  const currentIndex = $derived(visibleSteps.indexOf(step))
  const isLastVisible = $derived(currentIndex === visibleSteps.length - 1)
  // Every step must be valid before submit is allowed. Gates the review-screen Submit so it
  // can never silently no-op if an edit cleared a required field.
  const canSubmit = $derived(
    canGoNext(draft, 'animal') &&
      canGoNext(draft, 'status') &&
      canGoNext(draft, 'person') &&
      canGoNext(draft, 'unit'),
  )

  // Move focus to the new step's heading on step change (not on first render)
  // so keyboard and screen-reader users are carried to the new content instead
  // of being stranded on a now-vanished button.
  let stepContent = $state<HTMLDivElement>()
  let firstRender = true
  $effect(() => {
    step // read to track step changes
    reviewing // and the switch into/out of the review screen
    if (firstRender) {
      firstRender = false
      return
    }
    queueMicrotask(() => {
      stepContent?.querySelector('h2')?.focus()
    })
  })

  function next(): void {
    if (!canGoNext(draft, step)) return
    // Editing a field from the review screen returns straight to review on Next.
    if (cameFromReview) {
      cameFromReview = false
      reviewing = true
      return
    }
    const idx = visibleSteps.indexOf(step)
    if (idx < visibleSteps.length - 1) {
      const nextStep = visibleSteps[idx + 1]
      if (nextStep) step = nextStep
    } else if (prefilled) {
      // Last visible step in a prefilled flow -> confirm on the review screen before submit.
      reviewing = true
    }
  }

  function back(): void {
    // Sequential navigation cancels any pending edit-return; Next should advance, not jump to review.
    cameFromReview = false
    const idx = visibleSteps.indexOf(step)
    if (idx > 0) {
      const prevStep = visibleSteps[idx - 1]
      if (prevStep) step = prevStep
    }
  }

  function editStep(target: Step): void {
    if (!baseVisible.has(target)) {
      editedBack = new Set(editedBack).add(target)
    }
    reviewing = false
    cameFromReview = true
    step = target
  }

  async function submit(): Promise<void> {
    // Safety net: all steps must be valid. The navigation invariant already prevents reaching
    // submit/review with an incomplete step, but guard here too before writing.
    if (
      !canGoNext(draft, 'animal') ||
      !canGoNext(draft, 'status') ||
      !canGoNext(draft, 'person') ||
      !canGoNext(draft, 'unit')
    )
      return
    submitting = true
    try {
      const body = toIntakeBody(draft)
      const { data, error } = await submitIntake(
        propertyId ? { ...body, propertyId } : body,
        sessionToken,
      )
      if (error || !data) {
        showApiError(error)
        return
      }
      onComplete?.(data.confirmationId)
      const token = (data as { uploadToken?: string }).uploadToken
      const q = token
        ? `?id=${data.confirmationId}&upload=${encodeURIComponent(token)}`
        : `?id=${data.confirmationId}`
      navigate(`/thank-you${q}`, { replace: true })
    } finally {
      submitting = false
    }
  }
</script>

<div class="card" style={accentStyle}>
  <h1 class="sr-only">Register your pet</h1>
  {#if brandLoaded && pmSlug && !branding}
    <p class="brand-missing">
      This link isn't active. <a href="/register">Start a general registration.</a>
    </p>
  {/if}
  <ol class="progress" aria-label="Wizard progress">
    {#each visibleSteps as s, i (s)}
      <li
        class:current={!reviewing && s === step}
        class:done={reviewing || i < currentIndex}
        aria-current={!reviewing && s === step ? 'step' : undefined}
      >
        <span class="dot">{i + 1}</span>
        <span class="label">{STEP_LABELS[s]}</span>
      </li>
    {/each}
  </ol>

  <div class="step-content" bind:this={stepContent}>
    {#if reviewing}
      <ReviewStep
        {draft}
        unitLabel={draft.unitId !== null ? `Unit #${draft.unitId}` : 'No unit selected'}
        onEdit={editStep}
        onSubmit={submit}
        {submitting}
        {canSubmit}
      />
    {:else if step === 'animal'}
      {#if branding?.property.welcomeMessage}
        <p class="brand-welcome">{branding.property.welcomeMessage}</p>
      {/if}
      <AnimalStep bind:animal={draft.animal} />
    {:else if step === 'status'}
      <StatusStep bind:assistance={draft.assistance} species={draft.animal.species} />
    {:else if step === 'person'}
      <PersonStep bind:person={draft.person} />
    {:else if step === 'unit'}
      <UnitStep bind:unitId={draft.unitId} {propertyId} />
    {/if}
  </div>

  {#if !reviewing}
    <div class="actions">
      {#if currentIndex > 0}
        <button type="button" class="secondary" onclick={back}>Back</button>
      {/if}
      {#if isLastVisible && !prefilled}
        <button
          type="button"
          class="primary"
          onclick={submit}
          disabled={!canGoNext(draft, 'unit') || submitting}
        >
          {submitting ? 'Submitting…' : 'Submit'}
        </button>
      {:else}
        <button type="button" class="primary" onclick={next} disabled={!canGoNext(draft, step)}>
          {(isLastVisible && prefilled) || cameFromReview ? 'Review' : 'Next'}
        </button>
      {/if}
    </div>
  {/if}
</div>

<style>
  .card {
    background: var(--surface-2);
    padding: 32px;
    border-radius: 12px;
    box-shadow: 0 1px 3px rgba(0, 0, 0, 0.08);
  }
  .progress {
    display: flex;
    gap: 8px;
    padding: 0;
    margin: 0 0 24px;
    list-style: none;
  }
  .progress li {
    flex: 1;
    display: flex;
    align-items: center;
    gap: 8px;
    padding: 8px 12px;
    border-radius: 6px;
    background: var(--surface-3);
    /* In light, var(--muted) is only 4.3:1 on the var(--surface-3) chip (small text
       needs 4.5), so use a darker gray. In dark the chip is dark, so var(--muted)
       (warm light) is the readable choice; overrides below handle both themes. */
    color: #4b5563;
    font-size: 0.85rem;
  }
  /* Only the upcoming (neither current nor done) steps take the theme override;
     :not() keeps these from out-specifying .current/.done below. */
  @media (prefers-color-scheme: dark) {
    .progress li:not(.current):not(.done) {
      color: var(--muted);
    }
  }
  :root[data-theme='light'] .progress li:not(.current):not(.done) {
    color: #4b5563;
  }
  :root[data-theme='dark'] .progress li:not(.current):not(.done) {
    color: var(--muted);
  }
  .progress li.current {
    background: var(--brand-tint);
    color: var(--link);
    font-weight: 500;
  }
  .progress li.done {
    background: var(--ok-bg);
    color: var(--ok-ink);
  }
  .dot {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    width: 22px;
    height: 22px;
    border-radius: 50%;
    background: var(--surface-2);
    font-weight: 600;
    font-size: 0.8rem;
  }
  .actions {
    display: flex;
    justify-content: flex-end;
    gap: 8px;
    margin-top: 24px;
  }
  .brand-welcome {
    margin: 0 0 16px;
    color: var(--muted);
  }
  .brand-missing {
    margin: 0 0 16px;
    color: var(--muted);
    font-size: 0.9rem;
  }
  .sr-only {
    position: absolute;
    width: 1px;
    height: 1px;
    padding: 0;
    margin: -1px;
    overflow: hidden;
    clip: rect(0, 0, 0, 0);
    white-space: nowrap;
    border: 0;
  }
  button {
    padding: 10px 20px;
    min-height: 44px; /* comfortable touch target */
    border-radius: 4px;
    font-size: 0.95rem;
    cursor: pointer;
    border: 1px solid transparent;
  }
  button.primary {
    background: var(--brand);
    color: var(--on-brand, #fff);
    border-color: var(--brand);
  }
  button.primary:hover:not(:disabled) {
    background: var(--brand-hover);
  }
  button.primary:disabled {
    background: color-mix(in srgb, var(--brand) 40%, var(--surface-2));
    cursor: not-allowed;
  }
  button.secondary {
    background: var(--surface-2);
    color: var(--ink);
    border-color: var(--line-strong);
  }
  button.secondary:hover {
    background: var(--surface-3);
  }
</style>
